import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global React error boundary.
 * Catches all uncaught render errors below this component in the tree.
 * Errors are logged to console.error (captured by Cloudflare Workers tail logs
 * when the page is server-rendered, and visible in browser DevTools otherwise).
 *
 * Usage: wrap the root of the app (or a page subtree) with <ErrorBoundary>.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      "[ErrorBoundary] Uncaught render error:",
      error,
      info.componentStack,
    );
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-dvh flex items-center justify-center bg-theme-surface text-theme-primary p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <img
              src="/dcvfd-badge.svg"
              alt="DCVFD"
              width={56}
              height={56}
              className="mx-auto opacity-60"
            />

            <div className="space-y-2">
              <h1 className="text-xl font-bold text-theme-primary">
                Something went wrong
              </h1>
              <p className="text-sm text-theme-secondary">
                An unexpected error occurred. Your data is safe — this is a
                display issue only.
              </p>
            </div>

            {this.state.error && (
              <details className="text-left bg-theme-overlay border border-theme-subtle rounded-lg p-4">
                <summary className="text-xs font-mono text-theme-muted cursor-pointer select-none">
                  Error details
                </summary>
                <pre className="mt-2 text-xs text-status-red whitespace-pre-wrap break-all overflow-auto max-h-48">
                  {this.state.error.message}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-theme-subtle text-theme-secondary hover:text-theme-primary hover:bg-theme-overlay transition-colors"
              >
                Try again
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#0E403A] text-white hover:bg-[#0b322e] transition-colors"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
